import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Typography
} from '@mui/material';
import { FC, useCallback, useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ApiKeyForm } from './api-key-form';

export type ExchangeCredentialState = {
  liveApiKey?: string | null;
  liveApiSecret?: string | null;
  testnetApiKey?: string | null;
  testnetApiSecret?: string | null;
  liveApiKeyPopulated?: boolean;
  liveApiSecretPopulated?: boolean;
  testnetApiKeyPopulated?: boolean;
  testnetApiSecretPopulated?: boolean;
};

export type ApiManagementFormState = {
  exchanges: {
    exchangeId: string;
    apiKeys: ExchangeCredentialState;
  }[];
};

type ApiManagementFormProps = {
  state?: ApiManagementFormState;
  onSubmit: (config: ApiManagementFormState['exchanges'][number]) => void;
  onDelete: (exchangeId: string) => void;
};

export const ApiManagementForm: FC<ApiManagementFormProps> = ({
  onSubmit,
  onDelete,
  state = {}
}) => {
  const { control, handleSubmit, reset } = useForm<ApiManagementFormState>({
    defaultValues: state
  });

  const { fields, remove } = useFieldArray({
    control,
    name: 'exchanges'
  });

  useEffect(() => {
    reset(state);
  }, [state, reset]);

  const handleExchangeSubmit = useCallback(
    (index: number) =>
      handleSubmit((data) => {
        onSubmit(data.exchanges[index]);
      }),
    [handleSubmit, onSubmit]
  );

  const handleDeleteExchange = useCallback(
    (index: number) => {
      const exchangeId = fields[index].exchangeId;
      remove(index);
      onDelete(exchangeId);
    },
    [fields, remove, onDelete]
  );

  return (
    <Box>
      {fields.map((field, index) => {
        return (
          <Accordion key={field.id}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              id={`${field.exchangeId}-header`}
            >
              <Typography>{field.exchangeId}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ApiKeyForm
                title="Live"
                control={control}
                index={index}
                apiKeyName="liveApiKey"
                apiSecretName="liveApiSecret"
                onSubmit={handleExchangeSubmit(index)}
                onDelete={handleDeleteExchange}
                hasApiKeyValue={!!field.apiKeys.liveApiKeyPopulated}
                hasApiSecretValue={!!field.apiKeys.liveApiSecretPopulated}
              />
              <Divider sx={{ my: 3 }} />
              <ApiKeyForm
                title="Testnet"
                control={control}
                index={index}
                apiKeyName="testnetApiKey"
                apiSecretName="testnetApiSecret"
                onSubmit={handleExchangeSubmit(index)}
                onDelete={handleDeleteExchange}
                hasApiKeyValue={!!field.apiKeys.testnetApiKeyPopulated}
                hasApiSecretValue={!!field.apiKeys.testnetApiSecretPopulated}
              />
              <Controller
                name={`exchanges.${index}.exchangeId`}
                control={control}
                defaultValue={field.exchangeId}
                render={({ field }) => <input type="hidden" {...field} />}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
